import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Plus, Trash2, Edit2, Save, X, Loader2, Search, 
  RefreshCw, Play, CheckCircle, XCircle, Regex, Brain, 
  ArrowRight, Zap, Settings2, Download
} from "lucide-react";

const CATEGORIES = [
  { value: "contact", label: "Contact", color: "bg-blue-100 text-blue-800" },
  { value: "date", label: "Date", color: "bg-purple-100 text-purple-800" },
  { value: "financial", label: "Financier", color: "bg-green-100 text-green-800" },
  { value: "address", label: "Adresse", color: "bg-yellow-100 text-yellow-800" },
  { value: "order", label: "Commande", color: "bg-orange-100 text-orange-800" },
  { value: "general", label: "Général", color: "bg-zinc-100 text-zinc-800" },
];

export function ExtractionRulesManager() {
  const [rules, setRules] = useState([]);
  const [patternRules, setPatternRules] = useState([]);
  const [aiRules, setAiRules] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, rule: null });
  const [testModal, setTestModal] = useState({ open: false, rule: null });
  const [testText, setTestText] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pattern");

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API}/admin/extraction-rules`, {
      });
      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
        setPatternRules(data.pattern_rules || []);
        setAiRules(data.ai_rules || []);
        setAvailableColumns(data.available_columns || []);
      } else {
        toast.error("Erreur lors du chargement des règles");
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des règles");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportRules = async () => {
    setIsImporting(true);
    try {
      const response = await authFetch(`${API}/admin/import-rules`, {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadRules();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'import");
      }
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setIsImporting(false);
    }
  };

  const handleSave = async (ruleData) => {
    try {
      const isNew = !ruleData.rule_id;
      const url = isNew 
        ? `${API}/admin/extraction-rules`
        : `${API}/admin/extraction-rules/${ruleData.rule_id}`;
      
      const response = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ruleData)
      });

      if (response.ok) {
        toast.success(isNew ? "Règle créée" : "Règle mise à jour");
        setEditModal({ open: false, rule: null });
        loadRules();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Erreur lors de l'enregistrement");
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (ruleId) => {
    if (!confirm("Supprimer cette règle ?")) return;

    try {
      const response = await authFetch(`${API}/admin/extraction-rules/${ruleId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Règle supprimée");
        loadRules();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleReset = async () => {
    if (!confirm("Réinitialiser toutes les règles aux valeurs par défaut ? Cette action est irréversible.")) return;

    try {
      const response = await authFetch(`${API}/admin/extraction-rules/reset`, {
        method: "POST",
      });

      if (response.ok) {
        toast.success("Règles réinitialisées");
        loadRules();
      } else {
        toast.error("Erreur lors de la réinitialisation");
      }
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  const handleMigrate = async () => {
    try {
      const response = await authFetch(`${API}/admin/extraction-rules/migrate`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadRules();
      } else {
        toast.error("Erreur lors de la migration");
      }
    } catch (error) {
      toast.error("Erreur lors de la migration");
    }
  };

  const handleTest = async (regex) => {
    if (!testText.trim()) {
      toast.error("Entrez du texte à tester");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/extraction-rules/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          regex: regex,
          test_text: testText
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      } else {
        toast.error("Erreur lors du test");
      }
    } catch (error) {
      toast.error("Erreur lors du test");
    }
  };

  const filterRules = (rulesList) => {
    return rulesList.filter(r => {
      const matchesCategory = filterCategory === "all" || r.category === filterCategory;
      const matchesSearch = !searchQuery || 
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  };

  const getCategoryStyle = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.color || "bg-zinc-100 text-zinc-800";
  };

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.label || category;
  };

  const createNewRule = (type) => {
    setEditModal({ 
      open: true, 
      rule: {
        name: "",
        rule_type: type,
        regex: type === "pattern" ? "" : null,
        target_column: type === "pattern" ? "" : null,
        instruction: type === "ai_instruction" ? "" : null,
        description: "",
        category: "general",
        priority: 10,
        is_active: true
      }
    });
  };

  return (
    <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="extraction-rules-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Settings2 className="w-6 h-6 text-orange-500" />
          <div>
            <h2 className="text-xl font-semibold">Règles d'extraction</h2>
            <p className="text-sm text-zinc-500">Patterns et instructions pour l'extraction automatique</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleMigrate}
            className="text-zinc-600"
            title="Migrer les anciennes règles"
          >
            <Zap className="w-4 h-4 mr-1" />
            Migrer
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleReset}
            className="text-zinc-600"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Réinitialiser
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleImportRules}
            disabled={isImporting}
            className="text-blue-600 border-blue-300 hover:bg-blue-50"
          >
            {isImporting ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1" />
            )}
            Importer règles
          </Button>
        </div>
      </div>

      {/* Tabs for rule types */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="pattern" className="gap-2">
              <Regex className="w-4 h-4" />
              Patterns ({patternRules.length})
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-2">
              <Brain className="w-4 h-4" />
              Instructions IA ({aiRules.length})
            </TabsTrigger>
          </TabsList>
          
          <Button 
            size="sm" 
            onClick={() => createNewRule(activeTab === "pattern" ? "pattern" : "ai_instruction")}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nouvelle règle
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pattern Rules Tab */}
        <TabsContent value="pattern">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
          ) : filterRules(patternRules).length === 0 ? (
            <div className="text-center py-12 text-zinc-400">
              <Regex className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune règle pattern trouvée</p>
              <p className="text-sm mt-1">Créez des règles pour détecter automatiquement les données</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filterRules(patternRules).map((rule) => (
                <PatternRuleCard 
                  key={rule.rule_id}
                  rule={rule}
                  onEdit={() => setEditModal({ open: true, rule })}
                  onTest={() => {
                    setTestModal({ open: true, rule });
                    setTestText("");
                    setTestResults(null);
                  }}
                  onDelete={() => handleDelete(rule.rule_id)}
                  getCategoryStyle={getCategoryStyle}
                  getCategoryLabel={getCategoryLabel}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* AI Instructions Tab */}
        <TabsContent value="ai">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
          ) : filterRules(aiRules).length === 0 ? (
            <div className="text-center py-12 text-zinc-400">
              <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune instruction IA trouvée</p>
              <p className="text-sm mt-1">Ajoutez des instructions pour les cas complexes</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filterRules(aiRules).map((rule) => (
                <AIRuleCard 
                  key={rule.rule_id}
                  rule={rule}
                  onEdit={() => setEditModal({ open: true, rule })}
                  onDelete={() => handleDelete(rule.rule_id)}
                  getCategoryStyle={getCategoryStyle}
                  getCategoryLabel={getCategoryLabel}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, rule: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editModal.rule?.rule_id ? "Modifier la règle" : "Nouvelle règle"}
            </DialogTitle>
            <DialogDescription>
              {editModal.rule?.rule_type === "pattern" 
                ? "Définissez un pattern regex pour détecter automatiquement les données"
                : "Définissez une instruction pour l'IA"}
            </DialogDescription>
          </DialogHeader>
          
          {editModal.rule && (
            <RuleForm 
              rule={editModal.rule}
              availableColumns={availableColumns}
              onSave={handleSave}
              onCancel={() => setEditModal({ open: false, rule: null })}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={testModal.open} onOpenChange={(open) => !open && setTestModal({ open: false, rule: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tester le pattern</DialogTitle>
            <DialogDescription>
              {testModal.rule?.name} - {testModal.rule?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Regex</Label>
              <code className="text-sm bg-zinc-100 px-2 py-1 rounded font-mono text-purple-700 block mt-1">
                {testModal.rule?.regex}
              </code>
            </div>
            
            {testModal.rule?.target_column && (
              <div className="flex items-center gap-2 text-sm">
                <ArrowRight className="w-4 h-4 text-zinc-400" />
                <span>Colonne cible: <strong>{testModal.rule.target_column}</strong></span>
              </div>
            )}
            
            <div>
              <Label>Texte à tester</Label>
              <Textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="Entrez du texte pour tester le pattern..."
                className="mt-1"
                rows={4}
              />
            </div>

            <Button 
              onClick={() => handleTest(testModal.rule?.regex)}
              className="w-full"
            >
              <Play className="w-4 h-4 mr-2" />
              Tester
            </Button>

            {testResults && (
              <div className={`p-4 rounded-lg ${testResults.success && testResults.count > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                {testResults.success ? (
                  testResults.count > 0 ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">
                          {testResults.count} correspondance{testResults.count > 1 ? 's' : ''} trouvée{testResults.count > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {testResults.matches.slice(0, 5).map((match, i) => (
                          <div key={i} className="text-sm bg-white px-2 py-1 rounded border border-green-200">
                            <span className="font-mono text-green-700">{typeof match === 'string' ? match : JSON.stringify(match)}</span>
                          </div>
                        ))}
                        {testResults.count > 5 && (
                          <p className="text-xs text-green-600">+ {testResults.count - 5} autres...</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-amber-600" />
                      <span className="text-amber-800">Aucune correspondance trouvée</span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="text-red-800">{testResults.error}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Pattern Rule Card Component
function PatternRuleCard({ rule, onEdit, onTest, onDelete, getCategoryStyle, getCategoryLabel }) {
  return (
    <div className={`p-4 rounded-lg border ${rule.is_active ? 'bg-white border-zinc-200' : 'bg-zinc-50 border-zinc-100 opacity-60'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Regex className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <span className="font-medium text-zinc-900">{rule.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryStyle(rule.category)}`}>
              {getCategoryLabel(rule.category)}
            </span>
            {rule.target_column && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" />
                {rule.target_column}
                {(rule.extract_value || rule._extract_value) && (
                  <span className="font-semibold">= {rule.extract_value || rule._extract_value}</span>
                )}
              </span>
            )}
            <span className="text-xs text-zinc-400">P:{rule.priority}</span>
            {!rule.is_active && (
              <span className="text-xs text-red-500 font-medium">Inactif</span>
            )}
          </div>
          {rule.description && (
            <p className="text-sm text-zinc-500 mb-2">{rule.description}</p>
          )}
          <code className="text-xs bg-zinc-100 px-2 py-1 rounded font-mono text-purple-700 block overflow-x-auto">
            {rule.regex}
          </code>
        </div>
        <div className="flex items-center gap-1 ml-4 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={onTest} title="Tester">
            <Play className="w-4 h-4 text-green-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} title="Modifier">
            <Edit2 className="w-4 h-4 text-blue-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} title="Supprimer">
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// AI Rule Card Component
function AIRuleCard({ rule, onEdit, onDelete, getCategoryStyle, getCategoryLabel }) {
  return (
    <div className={`p-4 rounded-lg border ${rule.is_active ? 'bg-white border-zinc-200' : 'bg-zinc-50 border-zinc-100 opacity-60'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Brain className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="font-medium text-zinc-900">{rule.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryStyle(rule.category)}`}>
              {getCategoryLabel(rule.category)}
            </span>
            <span className="text-xs text-zinc-400">P:{rule.priority}</span>
            {!rule.is_active && (
              <span className="text-xs text-red-500 font-medium">Inactif</span>
            )}
          </div>
          {rule.description && (
            <p className="text-sm text-zinc-500 mb-2">{rule.description}</p>
          )}
          <div className="text-sm bg-blue-50 px-3 py-2 rounded border border-blue-100 text-blue-800 whitespace-pre-wrap">
            {rule.instruction}
          </div>
        </div>
        <div className="flex items-center gap-1 ml-4 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit} title="Modifier">
            <Edit2 className="w-4 h-4 text-blue-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} title="Supprimer">
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Rule Form Component
function RuleForm({ rule, availableColumns, onSave, onCancel }) {
  const [formData, setFormData] = useState({ ...rule });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Le nom est requis");
      return;
    }

    if (formData.rule_type === "pattern" && !formData.regex) {
      toast.error("La regex est requise pour les règles pattern");
      return;
    }

    if (formData.rule_type === "ai_instruction" && !formData.instruction) {
      toast.error("L'instruction est requise pour les règles IA");
      return;
    }

    setIsSaving(true);
    await onSave(formData);
    setIsSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Nom *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Téléphone FR, Export Suisse..."
          className="mt-1"
        />
      </div>

      {formData.rule_type === "pattern" ? (
        <>
          <div>
            <Label htmlFor="regex">Expression régulière (Regex) *</Label>
            <Input
              id="regex"
              value={formData.regex || ""}
              onChange={(e) => setFormData({ ...formData, regex: e.target.value })}
              placeholder="(?:\+33|0)\d{9}"
              className="mt-1 font-mono"
            />
          </div>

          <div>
            <Label htmlFor="target_column">Colonne cible</Label>
            <Select 
              value={formData.target_column || "__none__"} 
              onValueChange={(value) => setFormData({ ...formData, target_column: value === "__none__" ? null : value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Sélectionner une colonne..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune (détection uniquement)</SelectItem>
                {availableColumns.map(col => (
                  <SelectItem key={col} value={col}>{col}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-400 mt-1">
              Si définie, la valeur détectée sera automatiquement extraite vers cette colonne
            </p>
          </div>

          <div>
            <Label htmlFor="extract_value">Valeur à extraire (optionnel)</Label>
            <Input
              id="extract_value"
              value={formData.extract_value || ""}
              onChange={(e) => setFormData({ ...formData, extract_value: e.target.value })}
              placeholder="Ex: ECHANTILLONS, MARQUAGE..."
              className="mt-1"
            />
            <p className="text-xs text-zinc-400 mt-1">
              Si renseigné, cette valeur fixe sera utilisée au lieu de la valeur matchée par la regex
            </p>
          </div>
        </>
      ) : (
        <div>
          <Label htmlFor="instruction">Instruction *</Label>
          <Textarea
            id="instruction"
            value={formData.instruction || ""}
            onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
            placeholder="Ex: Si le client est en Suisse (+41), cocher EXPORT = true"
            className="mt-1 min-h-[120px]"
          />
          <p className="text-xs text-zinc-400 mt-1">
            Cette instruction sera transmise à l'IA lors de l'extraction
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          value={formData.description || ""}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Description de la règle..."
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Catégorie</Label>
          <Select 
            value={formData.category} 
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="priority">Priorité</Label>
          <Input
            id="priority"
            type="number"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 10 })}
            min={1}
            max={100}
            className="mt-1"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
        />
        <Label htmlFor="is_active">Actif</Label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </DialogFooter>
    </form>
  );
}

export default ExtractionRulesManager;
